// Vendor imports
const React = require('react')
// Client V2 common imports
const { keyCodesByName } = require('client-v2/common/utils/events')

// Default option values
const DEFAULT_ROW_HEIGHT = 48 // px
const DEFAULT_VISIBLE_ROW_COUNT = 5

// Keycode constants
const DISMISS_KEYCODE = keyCodesByName.escape
const UP_KEYCODE = keyCodesByName.arrowUp
const DOWN_KEYCODE = keyCodesByName.arrowDown
const SELECT_KEYCODE = keyCodesByName.enter
const captureKeyCodes = [
  DISMISS_KEYCODE,
  UP_KEYCODE,
  DOWN_KEYCODE,
  SELECT_KEYCODE
]

function useKeyboardSelection({
  activeIndex,
  rowCount,
  rowHeight = DEFAULT_ROW_HEIGHT,
  visibleRowCount = DEFAULT_VISIBLE_ROW_COUNT,
  onDismiss,
  onIndexFocus,
  onIndexSelect
}) {
  const ref = React.useRef(null)

  // Do nothing during server-side rendering
  if (typeof window === 'undefined') {
    return ref
  }

  const [ scrollPosition, setScrollPosition ] = React.useState(0)

  function scrollToPosition(newPosition) {
    setScrollPosition(newPosition)
    ref.current.scrollTop = newPosition * rowHeight
  }

  function moveFocusUp() {
    const newIndex = activeIndex === null ? -1 : activeIndex - 1
    // if index reaches the beginning of the list, scroll to the bottom
    if (newIndex < 0) {
      scrollToPosition(rowCount - visibleRowCount + 1)
      onIndexFocus(rowCount - 1)
      return
    }
    // if index is outside of the visible window, scroll one down
    if (newIndex < scrollPosition) {
      scrollToPosition(scrollPosition - 1)
    }
    onIndexFocus(newIndex)
  }

  function moveFocusDown() {
    const newIndex = activeIndex === null ? 0 : activeIndex + 1
    // if index reaches the end of the list, scroll to the top
    if (newIndex >= rowCount) {
      scrollToPosition(0)
      onIndexFocus(0)
      return
    }
    // if index is outside of the visible window, scroll one up
    if (newIndex > scrollPosition + visibleRowCount - 1) {
      scrollToPosition(scrollPosition + 1)
    }
    onIndexFocus(newIndex)
  }

  function onKeyDown(event) {
    const { keyCode } = event
    if (!rowCount || !captureKeyCodes.includes(keyCode)) {
      return
    }
    event.preventDefault()
    switch (keyCode) {
      case DISMISS_KEYCODE:
        onIndexFocus(null)
        onDismiss()
        break
      case SELECT_KEYCODE:
        onIndexSelect(activeIndex)
        break
      case UP_KEYCODE:
        moveFocusUp()
        break
      case DOWN_KEYCODE:
        moveFocusDown()
        break
    }
  }

  React.useEffect(() => {
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  })

  return ref
}

module.exports = useKeyboardSelection
